import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import {
  Table,
  Button,
  Icon,
  Header,
  Container,
  Loader,
  Message,
  Pagination,
  Input,
  Form,
  Grid,
  Popup,
  Checkbox
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import GlassTable from '../../components/Common/GlassTable';
import { UserResponse, CreateUserRequest, UpdateUserRequest } from '../../types/dtos';
import { apiService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../context/AuthContext';
import BaseModal, { BaseModalAction } from '../../components/shared/BaseModal';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';
import { formatPhone, maskCpf, maskPhone, cleanCpf, cleanPhone } from '../../utils/maskUtils';

/** Inteiro com sinal (grupo negativo ou id positivo). Vazio vira null. Não passa por cleanPhone. */
const parseTelegramChatId = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^-?\d+$/.test(trimmed)) {
    throw new Error('Chat ID do Telegram deve ser um inteiro, como -100123, ou ficar em branco.');
  }
  const value = Number(trimmed);
  if (!Number.isSafeInteger(value)) {
    throw new Error('Chat ID do Telegram está fora do intervalo numérico suportado.');
  }
  return value;
};

const readTelegramChatId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isSafeInteger(value)) return value;
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) {
    const parsed = Number(value.trim());
    return Number.isSafeInteger(parsed) ? parsed : null;
  }
  return null;
};

// Componente de proteção de rota para Admin
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  
  // Verificar se o usuário tem role de Admin
  const isAdmin = user?.roles && user.roles.includes('Admin');
  
  if (!isAdmin) {
    return (
      <Container style={{ marginTop: '50px', textAlign: 'center' }}>
        <Message error size="large">
          <Message.Header>Acesso Negado</Message.Header>
          <p>Você não tem permissão para acessar esta página. Apenas administradores podem gerenciar usuários.</p>
        </Message>
      </Container>
    );
  }
  
  return <>{children}</>;
};

const UsersPage: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={
        <AdminRoute>
          <UsersList />
        </AdminRoute>
      } />
    </Routes>
  );
};

// Componente Modal para Formulário de Usuário
const UserFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  user?: UserResponse;
  onSuccess: () => void;
}> = ({ open, onClose, user, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    roleIds: [] as string[],
    cpf: '',
    whatsAppPessoal: '',
    whatsAppCorporativo: '',
    telegramChatId: '',
    usuarioVtme: '',
    carteiraTreinamento: '',
    podeEditarLinhasContratoBloqueado: false
  });

  const [availableRoles, setAvailableRoles] = useState<any[]>([]);
  const { showSuccess, showError } = useToast();

  // Carregar roles disponíveis (usando endpoint otimizado)
  useEffect(() => {
    const loadRoles = async () => {
      try {
        // Usar endpoint otimizado para dropdown
        const roles = await apiService.getRolesDropdown();
        setAvailableRoles(roles.map((r: any) => ({
          key: r.id,
          text: r.name,
          value: r.id,  // Agora o value será o ID da role, não o nome
          description: r.description
        })));
      } catch (err) {
        console.error('Erro ao carregar roles:', err);
      }
    };
    loadRoles();
  }, []);

  // Preencher formulário quando estiver editando
  useEffect(() => {
    if (user && open) {
      setFormData({
        nome: user.nome,
        email: user.email,
        username: user.username || user.email.split('@')[0], // Usa username se existir
        password: '',
        confirmPassword: '',
        roleIds: user.userRoles?.map(ur => ur.roleId) || [],
        cpf: user.cpf || '',
        whatsAppPessoal: user.whatsAppPessoal || '',
        whatsAppCorporativo: user.whatsAppCorporativo || '',
        telegramChatId: user.telegramChatId != null ? String(user.telegramChatId) : '',
        usuarioVtme: user.usuarioVtme || '',
        carteiraTreinamento: user.carteiraTreinamento || '',
        podeEditarLinhasContratoBloqueado: user.podeEditarLinhasContratoBloqueado ?? false
      });
    } else if (!user && open) {
      // Reset form for create
      setFormData({
        nome: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        roleIds: [],
        cpf: '',
        whatsAppPessoal: '',
        whatsAppCorporativo: '',
        telegramChatId: '',
        usuarioVtme: '',
        carteiraTreinamento: '',
        podeEditarLinhasContratoBloqueado: false
      });
    }
  }, [user, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRolesChange = (e: any, { value }: any) => {
    setFormData(prev => ({
      ...prev,
      roleIds: value
    }));
  };

  const handleTelegramChatIdChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    data: { value?: string }
  ) => {
    const value = String(data?.value ?? e.target.value).trim();
    if (value !== '' && !/^-?\d*$/.test(value)) return;
    setFormData(prev => ({
      ...prev,
      telegramChatId: value
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    setError('');

    let telegramChatId: number | null;
    try {
      telegramChatId = parseTelegramChatId(formData.telegramChatId);
    } catch (err: any) {
      const message = err?.message || 'Chat ID do Telegram inválido';
      setError(message);
      showError(message);
      return;
    }

    setLoading(true);

    try {
      if (user) {
        // Edit user - por enquanto só vamos implementar create
        const updateData: UpdateUserRequest = {
          nome: formData.nome,
          email: formData.email,
          username: formData.username,
          roleIds: formData.roleIds,
          cpf: cleanCpf(formData.cpf),
          whatsAppPessoal: cleanPhone(formData.whatsAppPessoal),
          whatsAppCorporativo: cleanPhone(formData.whatsAppCorporativo),
          telegramChatId,
          usuarioVtme: formData.usuarioVtme.trim() || undefined,
          carteiraTreinamento: formData.carteiraTreinamento.trim() || undefined,
          podeEditarLinhasContratoBloqueado: formData.podeEditarLinhasContratoBloqueado
        };

        await apiService.updateUser(user.id, updateData);
        showSuccess('Usuário atualizado com sucesso!');
        onSuccess();
        onClose();

      } else {
        // Create user
        const createData: CreateUserRequest = {
          nome: formData.nome,
          email: formData.email,
          username: formData.username,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          roleIds: formData.roleIds,
          cpf: cleanCpf(formData.cpf),
          whatsAppPessoal: cleanPhone(formData.whatsAppPessoal),
          whatsAppCorporativo: cleanPhone(formData.whatsAppCorporativo),
          telegramChatId,
          usuarioVtme: formData.usuarioVtme.trim() || undefined,
          carteiraTreinamento: formData.carteiraTreinamento.trim() || undefined,
          podeEditarLinhasContratoBloqueado: formData.podeEditarLinhasContratoBloqueado
        };

        await apiService.createUser(createData);
        showSuccess('Usuário criado com sucesso!');
        onSuccess();
        onClose();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao salvar usuário';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isEdit = !!user;

  // Definição das ações (botões) para o BaseModal
  const actions: BaseModalAction[] = [
    {
      key: 'cancel',
      content: 'Cancelar',
      icon: 'cancel',
      onClick: onClose,
    },
    {
      key: 'save',
      content: isEdit ? 'Salvar Alterações' : 'Criar Usuário',
      icon: 'save',
      primary: true,
      loading: loading,
      onClick: () => handleSubmit(),
    },
  ];

  return (
    <BaseModal
      isOpen={open}
      onClose={onClose}
      title={isEdit ? 'Editar Usuário' : 'Novo Usuário'}
      icon={isEdit ? "edit" : "user plus"}
      size="large"
      actions={actions}
    >
      <Form onSubmit={handleSubmit} loading={loading}>
        <Grid stackable>
          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Input
                fluid
                label="Nome Completo"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                required
                placeholder="Digite o nome completo"
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <Form.Input
                fluid
                label="E-mail"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="Digite o e-mail"
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Input
                fluid
                label="Nome de Usuário"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                placeholder="Digite o nome de usuário"
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <Form.Dropdown
                fluid
                label="Papéis/Funções"
                placeholder="Selecione os papéis"
                multiple
                selection
                options={availableRoles}
                value={formData.roleIds}
                onChange={handleRolesChange}
              />
            </Grid.Column>
          </Grid.Row>

          {!isEdit && (
            <Grid.Row>
              <Grid.Column width={8}>
                <Form.Input
                  fluid
                  label="Senha"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Digite a senha"
                />
              </Grid.Column>
              <Grid.Column width={8}>
                <Form.Input
                  fluid
                  label="Confirmar Senha"
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  placeholder="Confirme a senha"
                />
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row>
            <Grid.Column width={8}>
              <Form.Input
                fluid
                label="CPF"
                name="cpf"
                value={formData.cpf}
                onChange={handleInputChange}
                placeholder="Somente números (11 dígitos)"
                maxLength={11}
              />
            </Grid.Column>
            <Grid.Column width={8}>
              <Form.Input
                fluid
                label="WhatsApp Corporativo"
                name="whatsAppCorporativo"
                value={formData.whatsAppCorporativo}
                onChange={handleInputChange}
                placeholder="5541999999999"
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Input
                fluid
                label="WhatsApp Pessoal (Confidencial)"
                name="whatsAppPessoal"
                value={formData.whatsAppPessoal}
                onChange={handleInputChange}
                placeholder="5541999999999"
                icon="lock"
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Input
                fluid
                label="Chat ID do Telegram"
                name="telegramChatId"
                value={formData.telegramChatId}
                onChange={handleTelegramChatIdChange}
                placeholder="-100123 (grupo) ou id positivo"
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Input
                fluid
                label="Usuário VTME"
                name="usuarioVtme"
                value={formData.usuarioVtme}
                onChange={handleInputChange}
                placeholder="Login do usuário no VTME"
                maxLength={100}
              />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Field>
                <label>
                  Carteira de Treinamento{' '}
                  <Popup
                    trigger={<Icon name="info circle" style={{ cursor: 'help' }} />}
                    content="Nome enviado na solicitação WhatsApp de troca de carteira quando o usuário está em time de treinamento. Se vazio, usa o nome do usuário."
                    inverted
                  />
                </label>
                <Input
                  fluid
                  name="carteiraTreinamento"
                  value={formData.carteiraTreinamento}
                  onChange={handleInputChange}
                  placeholder="Nome do consultor/supervisor no VTME"
                  maxLength={255}
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column width={16}>
              <Form.Field>
                <Checkbox
                  toggle
                  label="Pode editar linhas de contrato bloqueado (bypass status/histórico)"
                  name="podeEditarLinhasContratoBloqueado"
                  checked={formData.podeEditarLinhasContratoBloqueado}
                  onChange={(_, { checked }) =>
                    setFormData(prev => ({
                      ...prev,
                      podeEditarLinhasContratoBloqueado: checked ?? false
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>

          {error && (
            <Grid.Row>
              <Grid.Column width={16}>
                <Message error>
                  <p>{error}</p>
                </Message>
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </Form>
    </BaseModal>
  );
};

// Componente de listagem de usuários
const UsersList: React.FC = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [activePage, setActivePage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);

  // Estados para filtros
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ name: '', email: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserResponse | undefined>();
  const { showSuccess, showError } = useToast();

  const loadUsers = async () => {
    try {
      setLoading(true);
      const filters: any = {
        page: activePage,
        pageSize
      };

      if (appliedFilters.name) {
        filters.name = appliedFilters.name;
      }

      if (appliedFilters.email) {
        filters.email = appliedFilters.email;
      }

      const response = await apiService.getUsers(filters);

      // Mapear os dados da API para o formato esperado pelo componente
      const mappedUsers: UserResponse[] = response.items.map((user: any) => ({
        id: user.id, // Mantém como string (Guid do backend)
        nome: user.nome,
        email: user.email,
        username: user.username,
        accessToken: user.accessToken,
        tokenExpiration: user.tokenExpiration ? new Date(user.tokenExpiration) : undefined,
        createdAt: new Date(user.createdAt),
        updatedAt: user.updatedAt ? new Date(user.updatedAt) : undefined,
        updatedBy: user.updatedBy,
        isDeleted: user.isDeleted,
        deletedBy: user.deletedBy,
        deletedAt: user.deletedAt ? new Date(user.deletedAt) : undefined,
        isActive: user.isActive, // Calcula baseado em isDeleted
        userRoles: user.userRoles || [],
        teamId: user.teamId,
        cpf: user.cpf,
        whatsAppPessoal: user.whatsAppPessoal,
        whatsAppCorporativo: user.whatsAppCorporativo,
        telegramChatId: readTelegramChatId(user.telegramChatId),
        usuarioVtme: user.usuarioVtme,
        carteiraTreinamento: user.carteiraTreinamento,
        podeEditarLinhasContratoBloqueado: user.podeEditarLinhasContratoBloqueado ?? false
      }));

      setUsers(mappedUsers);
      setTotalPages(response.totalPages);
    } catch (err: any) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários');
      showError('Erro ao carregar lista de usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, appliedFilters]);

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await apiService.deleteUser(id);
        showSuccess('Usuário excluído com sucesso!');
        loadUsers();
      } catch (err) {
        showError('Erro ao excluir usuário. Tente novamente.');
      }
    }
  };

  const handleActivateUser = async (id: string) => {
    try {
      await apiService.activateUser(id);
      showSuccess('Usuário ativado com sucesso!');
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao ativar usuário';
      showError(errorMessage);
    }
  };

  const handleDeactivateUser = async (id: string) => {
    try {
      await apiService.deactivateUser(id);
      showSuccess('Usuário desativado com sucesso!');
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erro ao desativar usuário';
      showError(errorMessage);
    }
  };

  const handleResetPassword = async (id: string, userName: string) => {
    if (window.confirm(`Tem certeza que deseja resetar a senha do usuário "${userName}" para o padrão (Gettel1245@)?`)) {
      try {
        await apiService.resetUserPassword(id);
        showSuccess('Senha resetada com sucesso para o padrão (Gettel1245@)!');
        loadUsers();
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Erro ao resetar senha';
        showError(errorMessage);
      }
    }
  };

  const handleCreateUser = () => {
    setEditingUser(undefined);
    setModalOpen(true);
  };

  const handleEditUser = (user: UserResponse) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingUser(undefined);
  };

  const handleFormSuccess = () => {
    loadUsers(); // Recarregar a lista após criar/editar
  };

  // Funções de filtro
  const handleApplyFilters = () => {
    setAppliedFilters({ name: nameFilter, email: emailFilter });
    setActivePage(1); // Voltar para a primeira página ao aplicar filtros
  };

  const handleClearFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setAppliedFilters({ name: '', email: '' });
    setActivePage(1);
  };

  const handleFilterKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  if (loading) {
    return (
      <GlassPageWrapper>
        <Loader active>Carregando usuários...</Loader>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Gerenciamento de Usuários" icon="users">

      {error && (
        <Message error>
          <p>{error}</p>
        </Message>
      )}

      <div style={{
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Input
            icon="user"
            placeholder="Filtrar por nome..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            onKeyPress={handleFilterKeyPress}
            style={{ width: '250px' }}
          />

          <Input
            icon="mail"
            placeholder="Filtrar por e-mail..."
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            onKeyPress={handleFilterKeyPress}
            style={{ width: '250px' }}
          />

          <Button 
            primary
            onClick={handleApplyFilters}
            style={getDefaultButtonStyle()}
            {...getButtonHoverEffects('default')}
          >
            <Icon name="search" />
            Buscar
          </Button>

          {(appliedFilters.name || appliedFilters.email) && (
            <Button 
              secondary
              onClick={handleClearFilters}
              style={getDefaultButtonStyle()}
              {...getButtonHoverEffects('secondary')}
            >
              <Icon name="remove" />
              Limpar Filtros
            </Button>
          )}
        </div>

        <Button 
          primary 
          onClick={handleCreateUser}
          style={getDefaultButtonStyle()}
          {...getButtonHoverEffects('default')}
        >
          <Icon name="plus" />
          Novo Usuário
        </Button>
      </div>

      <GlassTable celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={2}>Nome</Table.HeaderCell>
            <Table.HeaderCell width={2}>Usuário (login)</Table.HeaderCell>
            <Table.HeaderCell width={2}>E-mail</Table.HeaderCell>
            <Table.HeaderCell width={1}>Status</Table.HeaderCell>
            <Table.HeaderCell width={2}>WhatsApp Corp.</Table.HeaderCell>
            <Table.HeaderCell width={1}>Chat Telegram</Table.HeaderCell>
            <Table.HeaderCell width={2}>Usuário VTME</Table.HeaderCell>
            <Table.HeaderCell width={2}>Papéis</Table.HeaderCell>
            <Table.HeaderCell width={2}>Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {users.map((user) => (
            <Table.Row key={user.id}>
              <Table.Cell>{user.nome}</Table.Cell>
              <Table.Cell>{user.username || '-'}</Table.Cell>
              <Table.Cell>{user.email}</Table.Cell>
              <Table.Cell>
                <span style={{
                  color: user.isActive ? 'green' : 'red',
                  fontWeight: 'bold'
                }}>
                  {user.isActive ? 'Ativo' : 'Inativo'}
                </span>
              </Table.Cell>
              <Table.Cell>
                {user.whatsAppCorporativo ? formatPhone(user.whatsAppCorporativo) : '-'}
              </Table.Cell>
              <Table.Cell>{user.telegramChatId != null ? user.telegramChatId : '-'}</Table.Cell>
              <Table.Cell>{user.usuarioVtme || '-'}</Table.Cell>
              <Table.Cell>
                {user.userRoles.map((userRole, index) => (
                  <span key={index}>
                    {userRole.role?.name || 'N/A'}
                    {index < user.userRoles.length - 1 && ', '}
                  </span>
                ))}
              </Table.Cell>
              <Table.Cell>
                <Button.Group size="small">
                  <Button
                    icon="edit"
                    title="Editar usuário"
                    onClick={() => handleEditUser(user)}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                  <Button
                    icon="key"
                    title="Resetar senha para padrão"
                    onClick={() => handleResetPassword(user.id, user.nome)}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem',
                      background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                  {user.isActive ? (
                    <Button
                      icon="pause"
                      title="Desativar usuário"
                      onClick={() => handleDeactivateUser(user.id)}
                      style={{
                        ...getDefaultButtonStyle(),
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                      }}
                      {...getButtonHoverEffects('default')}
                    />
                  ) : (
                    <Button
                      icon="play"
                      title="Ativar usuário"
                      onClick={() => handleActivateUser(user.id)}
                      style={{
                        ...getDefaultButtonStyle(),
                        padding: '0.4rem 0.8rem',
                        fontSize: '0.8rem',
                        background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)'
                      }}
                      {...getButtonHoverEffects('default')}
                    />
                  )}
                  <Button
                    icon="trash"
                    title="Excluir usuário"
                    onClick={() => handleDelete(user.id)}
                    style={{
                      ...getDefaultButtonStyle(),
                      padding: '0.4rem 0.8rem',
                      fontSize: '0.8rem',
                      background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                    }}
                    {...getButtonHoverEffects('default')}
                  />
                </Button.Group>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </GlassTable>

      {totalPages > 1 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Pagination
            activePage={activePage}
            totalPages={totalPages}
            onPageChange={(_, data) => setActivePage(data.activePage as number)}
          />
        </div>
      )}

      {/* Modal de Formulário */}
      <UserFormModal
        open={modalOpen}
        onClose={handleModalClose}
        user={editingUser}
        onSuccess={handleFormSuccess}
      />
    </GlassPageWrapper>
  );
};



export default UsersPage;
